/**
 * useRetroactiveOnboarding Composable
 *
 * Task Group 6.3: Retroactive onboarding trigger for returning participants
 *
 * This composable handles the detection and triggering of AIPM onboarding
 * for participants who:
 * - Enrolled in a program before AIPM onboarding existed
 * - Dismissed onboarding previously but the dismissal window has expired
 * - Are returning to the platform after a period of inactivity
 *
 * Key behaviors:
 * - Checks UserSettings.onboardingCompletedAt on authenticated page load
 * - Respects lastDismissedAt (24-hour dismissal window)
 * - Uses BehavioralAnalytics service for "return_visit" event detection
 * - Different messaging than checkout flow for returning participants
 */

import { reactive, computed, readonly } from 'vue';
import {
  useAIPMOnboarding,
  isPermanentlyDismissed,
  type OnboardingEntryPoint,
  type CoordinatorSettings
} from './useAIPMOnboarding';

// ============================================================
// Types
// ============================================================

/**
 * User settings for onboarding status
 */
export interface UserOnboardingSettings {
  userId: string;
  onboardingCompletedAt: Date | null;
  personaId: string | null;
  lastDismissedAt?: Date | null;
}

/**
 * Context for retroactive onboarding trigger decision
 */
export interface RetroactiveTriggerContext {
  isAuthenticated: boolean;
  isParticipant: boolean;
  programId: string;
  userSettings: UserOnboardingSettings | null;
  isFirstVisitThisSession: boolean;
}

/**
 * Return visit event payload for BehavioralAnalytics
 */
export interface ReturnVisitEventPayload {
  eventType: 'return_visit';
  programId: string;
  timestamp: Date;
  context: {
    entryPoint: OnboardingEntryPoint;
    onboardingTriggered: boolean;
  };
  sessionContext?: {
    daysSinceLastVisit?: number;
    previousSessionCount?: number;
  };
}

/**
 * Retroactive onboarding state
 */
interface RetroactiveOnboardingState {
  isChecking: boolean;
  hasTriggeredThisSession: boolean;
  programsChecked: Set<string>;
}

// ============================================================
// Constants
// ============================================================

const DISMISSAL_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours
const SESSION_DISMISSALS_KEY = 'aipm_session_dismissals';

// ============================================================
// Welcome Messages
// ============================================================

const WELCOME_MESSAGES = {
  checkout: {
    title: 'Meet Your AI Mentor',
    message:
      "Let's get started! Your AI mentor will help guide you through this program and support your success."
  },
  retroactive: {
    title: "We've Been Here All Along",
    message:
      "You may not have noticed, but there's been an AI mentor working behind the scenes to support your journey. Let's officially meet and personalize your experience."
  },
  unsponsored: {
    title: 'Your AI Mentor Awaits',
    message: "Let's get started! Your AI mentor is ready to help you make the most of this program."
  },
  participant_initiated: {
    title: 'Customize Your AI Mentor',
    message: "Ready to personalize your AI mentor experience? Let's set up your preferences."
  }
};

// ============================================================
// Singleton Instance
// ============================================================

// eslint-disable-next-line no-use-before-define -- createRetroactiveOnboardingComposable is defined below, this is just a type reference
let retroactiveInstance: ReturnType<typeof createRetroactiveOnboardingComposable> | null = null;

/**
 * Create the retroactive onboarding composable
 */
function createRetroactiveOnboardingComposable() {
  /* eslint-disable no-use-before-define */
  const state = reactive<RetroactiveOnboardingState>({
    isChecking: false,
    hasTriggeredThisSession: false,
    programsChecked: new Set()
  });

  // Get the AIPM onboarding composable
  const aipmOnboarding = useAIPMOnboarding();

  // ============================================================
  // Session Dismissal Tracking
  // ============================================================

  /**
   * Get session dismissals from sessionStorage
   */
  function getSessionDismissals(): string[] {
    try {
      const stored = sessionStorage.getItem(SESSION_DISMISSALS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /**
   * Record a session dismissal
   */
  function recordSessionDismissal(programId: string): void {
    const dismissals = getSessionDismissals();
    if (!dismissals.includes(programId)) {
      dismissals.push(programId);
      sessionStorage.setItem(SESSION_DISMISSALS_KEY, JSON.stringify(dismissals));
    }
  }

  /**
   * Check if program was dismissed in current session
   */
  function isSessionDismissed(programId: string): boolean {
    const dismissals = getSessionDismissals();
    return dismissals.includes(programId);
  }

  // ============================================================
  // Trigger Logic
  // ============================================================

  /**
   * Check if onboarding should be triggered for a given user settings
   */
  function shouldTriggerOnboarding(
    userSettings: UserOnboardingSettings | null,
    programId: string
  ): boolean {
    // If no user settings, this is a new user - let checkout flow handle it
    if (!userSettings) {
      return false;
    }

    // Already completed onboarding
    if (userSettings.onboardingCompletedAt) {
      return false;
    }

    // Check permanent dismissal (localStorage per-program)
    if (isPermanentlyDismissed(programId)) {
      return false;
    }

    // Check session dismissal
    if (isSessionDismissed(programId)) {
      return false;
    }

    return true;
  }

  /**
   * Check if checkout should be blocked due to required onboarding
   */
  function isCheckoutBlocked(
    userSettings: UserOnboardingSettings | null,
    coordinatorSettings: CoordinatorSettings
  ): boolean {
    // If onboarding is skippable, never block
    if (coordinatorSettings.skippable) {
      return false;
    }

    // If no user settings or not completed, block
    if (!userSettings || !userSettings.onboardingCompletedAt) {
      return true;
    }

    return false;
  }

  /**
   * Check if should trigger retroactive onboarding based on full context
   */
  function shouldTriggerRetroactiveOnboarding(context: RetroactiveTriggerContext): boolean {
    // Must be authenticated
    if (!context.isAuthenticated) {
      return false;
    }

    // Must be a participant in the program
    if (!context.isParticipant) {
      return false;
    }

    // Check if already triggered in this session for this program
    if (state.programsChecked.has(context.programId)) {
      return false;
    }

    // Check permanent dismissal (localStorage per-program)
    if (isPermanentlyDismissed(context.programId)) {
      return false;
    }

    // Check session dismissal
    if (isSessionDismissed(context.programId)) {
      return false;
    }

    // No user settings - new user, don't trigger retroactive
    if (!context.userSettings) {
      return false;
    }

    // Already completed onboarding
    if (context.userSettings.onboardingCompletedAt) {
      return false;
    }

    // Check server-side dismissal window (24 hours)
    if (context.userSettings.lastDismissedAt) {
      const dismissedAt = new Date(context.userSettings.lastDismissedAt).getTime();
      const now = Date.now();
      if (now - dismissedAt < DISMISSAL_WINDOW_MS) {
        return false;
      }
    }

    return true;
  }

  // ============================================================
  // Trigger Actions
  // ============================================================

  /**
   * Trigger retroactive onboarding for a program
   */
  async function triggerRetroactiveOnboarding(programId: string): Promise<void> {
    // Mark as checked
    state.programsChecked.add(programId);

    // Track the return visit event
    trackReturnVisitEvent({
      eventType: 'return_visit',
      programId,
      timestamp: new Date(),
      context: {
        entryPoint: 'retroactive',
        onboardingTriggered: true
      }
    });

    // Start onboarding with retroactive entry point
    await aipmOnboarding.startOnboarding(programId, 'retroactive');
  }

  /**
   * Trigger onboarding from checkout flow
   */
  async function triggerCheckoutOnboarding(programId: string): Promise<void> {
    await aipmOnboarding.startOnboarding(programId, 'checkout');
  }

  // ============================================================
  // BehavioralAnalytics Integration
  // ============================================================

  /**
   * Track return visit event for behavioral analytics
   */
  function trackReturnVisitEvent(payload: ReturnVisitEventPayload): void {
    // This would integrate with the BehavioralAnalytics service
    // For now, we log and could emit to a tracking endpoint
    console.log('[useRetroactiveOnboarding] Return visit event:', payload);

    // In a real implementation, this would call the BehavioralAnalytics API
    // fetchBehavioralAnalytics or a dedicated event tracking endpoint
  }

  /**
   * Build return visit event payload
   */
  function buildReturnVisitEventPayload(
    programId: string,
    sessionContext?: { daysSinceLastVisit?: number; previousSessionCount?: number }
  ): ReturnVisitEventPayload {
    return {
      eventType: 'return_visit',
      programId,
      timestamp: new Date(),
      context: {
        entryPoint: 'retroactive',
        onboardingTriggered: true
      },
      sessionContext
    };
  }

  // ============================================================
  // Welcome Message Customization
  // ============================================================

  /**
   * Get welcome message based on entry point
   */
  function getWelcomeMessage(entryPoint: OnboardingEntryPoint): string {
    const messages = WELCOME_MESSAGES[entryPoint] || WELCOME_MESSAGES.participant_initiated;
    return `${messages.title}: ${messages.message}`;
  }

  /**
   * Get welcome title based on entry point
   */
  function getWelcomeTitle(entryPoint: OnboardingEntryPoint): string {
    return (WELCOME_MESSAGES[entryPoint] || WELCOME_MESSAGES.participant_initiated).title;
  }

  /**
   * Get welcome description based on entry point
   */
  function getWelcomeDescription(entryPoint: OnboardingEntryPoint): string {
    return (WELCOME_MESSAGES[entryPoint] || WELCOME_MESSAGES.participant_initiated).message;
  }

  // ============================================================
  // State Accessors
  // ============================================================

  const isChecking = computed(() => state.isChecking);
  const hasTriggered = computed(() => state.hasTriggeredThisSession);

  // ============================================================
  // Reset
  // ============================================================

  function reset(): void {
    state.isChecking = false;
    state.hasTriggeredThisSession = false;
    state.programsChecked.clear();
  }

  // ============================================================
  // Return API
  // ============================================================

  return {
    // State
    state: readonly(state),
    isChecking,
    hasTriggered,

    // Session dismissal
    getSessionDismissals,
    recordSessionDismissal,
    isSessionDismissed,

    // Trigger checks
    shouldTriggerOnboarding,
    shouldTriggerRetroactiveOnboarding,
    isCheckoutBlocked,

    // Trigger actions
    triggerRetroactiveOnboarding,
    triggerCheckoutOnboarding,

    // BehavioralAnalytics
    trackReturnVisitEvent,
    buildReturnVisitEventPayload,

    // Welcome messages
    getWelcomeMessage,
    getWelcomeTitle,
    getWelcomeDescription,

    // Reset
    reset
  };
  /* eslint-enable no-use-before-define */
}

// ============================================================
// Public API
// ============================================================

/**
 * useRetroactiveOnboarding - Singleton composable for retroactive onboarding triggers
 *
 * This ensures consistent state across all uses of the retroactive trigger.
 */
export function useRetroactiveOnboarding() {
  if (!retroactiveInstance) {
    retroactiveInstance = createRetroactiveOnboardingComposable();
  }
  return retroactiveInstance;
}

/**
 * Reset the singleton instance (for testing)
 */
export function resetRetroactiveOnboardingInstance(): void {
  if (retroactiveInstance) {
    retroactiveInstance.reset();
  }
  retroactiveInstance = null;
}

export default useRetroactiveOnboarding;
